import { Star } from 'components/icons';
import { PinMark } from 'layout/NewDesignLayout/Sidebar/Pinned';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { Pin, searchResultPin } from 'utils/pins';
import { type TourSample, tourSample } from './tourMode';

const ROW = 'text-caption absolute left-2 flex h-5 items-center gap-2 rounded-[5px] pr-1.5 whitespace-nowrap';
// The chips and the dots show only while their keyframes run: without them, under reduced motion or not, the demo
// is the still of three pins, never a ring of grey dots.
const CHIP =
    'text-caption shadow-drag absolute top-0 left-0 flex h-[22px] items-center gap-1.5 rounded-md outline-hidden pr-2 pl-1.5 font-medium whitespace-nowrap opacity-0';
/** Where the puff's six dots stand around the chip let go out of Pinned. */
const DOTS = [
    'top-[19px] left-[198px]',
    'top-3 left-[209px]',
    'top-[19px] left-[220px]',
    'top-[31px] left-[220px]',
    'top-[38px] left-[209px]',
    'top-[31px] left-[198px]',
];
/** Outside a tour, as on the Welcome, the tour's own sample pins and contact, written out: its fixtures load lazily. */
const OUTSIDE_TOUR: Pick<TourSample, 'pins' | 'recentlyViewed'> = {
    pins: [
        { key: 'matter:900002', label: '2026-0398 · Charter party dispute', url: '/matter/900002' },
        { key: 'view:900501', label: 'Open, hourly', url: '/matters', view: { listKey: 'matters', id: 900501 } },
    ],
    recentlyViewed: [{ entityType: 'PARTY', id: 900101, displayName: 'Lumen Grid Technologies OÜ', viewedAt: 0 }],
};

/**
 * The Drag to pin step's demo, under the card's body, and the Welcome's pinning tile's: a mini Pinned with two sample
 * pins, where a sample contact's chip docks with one wiggle and lands as a third pin, then is dragged out and goes in
 * a puff: 6s, played twice, then it rests on the still of the three pins. Markup and keyframes only; under reduced
 * motion that still from the start.
 */
export default function PinDragDemo() {
    const sample = tourSample() ?? OUTSIDE_TOUR;
    const contact = sample.recentlyViewed.find(({ entityType }) => entityType === 'PARTY');
    const added = contact && searchResultPin(contact.entityType, contact.id, contact.displayName);
    if (!added) return null;
    const row = (pin: Pin, top: string, className?: string) => (
        <span key={pin.key} className={cn(ROW, top, className)}>
            <PinMark pin={pin} />
            {pin.label}
        </span>
    );

    return (
        <div aria-hidden className="bg-sidebar text-sidebar-foreground relative h-24 overflow-hidden rounded-lg">
            <span className="text-label text-sidebar-muted absolute top-2 left-2.5 uppercase">{t('menu.pinned')}</span>
            {sample.pins.slice(0, 2).map((pin, position) => row(pin, position === 0 ? 'top-6' : 'top-[46px]'))}
            {row(added, 'top-[68px]', 'pin-demo-row')}
            <span className={cn(CHIP, 'pin-demo-in border-changed-border bg-card')}>
                <PinMark pin={added} />
                {added.label}
                <Star className="text-secondary size-3" />
            </span>
            <span className={cn(CHIP, 'pin-demo-out bg-danger-tint border-destructive/40')}>
                <PinMark pin={added} />
                {added.label}
                <span className="text-destructive font-semibold">{t('pins.unpin')}</span>
            </span>
            {DOTS.map((place) => (
                <span
                    key={place}
                    className={cn('pin-demo-dot bg-border-dashed absolute size-[5px] rounded-full opacity-0', place)}
                />
            ))}
        </div>
    );
}
